import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getActiveCampaignLists,
  seedActiveCampaignOutbound,
  updateActiveCampaignSettings,
} from '../api/importStep';
import type { ActiveCampaignListsResponse } from '../types';

interface UseActiveCampaignImportOptions {
  /** Gate the lists fetch -- only meaningful once the integration is connected. */
  enabled?: boolean;
}

export function useActiveCampaignImport(
  orgId: number | string | undefined,
  options?: UseActiveCampaignImportOptions
) {
  const queryClient = useQueryClient();

  const listsQuery = useQuery<ActiveCampaignListsResponse>({
    queryKey: ['activeCampaignLists', orgId],
    queryFn: () => {
      if (!orgId) {
        throw new Error('orgId is required');
      }
      return getActiveCampaignLists(orgId);
    },
    enabled: !!orgId && (options?.enabled ?? true),
  });

  const updateDefaultList = useMutation({
    mutationFn: async (defaultListId: string) => {
      if (!orgId) {
        throw new Error('orgId is required');
      }
      return updateActiveCampaignSettings(orgId, {
        default_list_id: defaultListId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['activeCampaignStatus', orgId],
      });
    },
  });

  const seedOutbound = useMutation({
    mutationFn: async () => {
      if (!orgId) {
        throw new Error('orgId is required');
      }
      return seedActiveCampaignOutbound(orgId);
    },
  });

  return {
    lists: listsQuery,
    updateDefaultList,
    seedOutbound,
  };
}
